import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarOff, Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";

const smallInput = "rounded-2xl border border-[var(--border)] bg-[var(--bg)] px-4 py-2.5 text-sm outline-none transition-colors focus:border-[var(--mint-dark)] focus:bg-white";
const labelClass = "text-xs font-semibold uppercase tracking-wide";
const today = () => new Date().toISOString().split("T")[0];
const WEEKDAYS = [["L", "Lunes"], ["M", "Martes"], ["X", "Miércoles"], ["J", "Jueves"], ["V", "Viernes"], ["S", "Sábado"], ["D", "Domingo"]];
const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];

function WeekdayToggles({ rangeId, weekdays, onChange }) {
  const toggle = (d) => onChange(weekdays.includes(d) ? weekdays.filter((x) => x !== d) : [...weekdays, d].sort());
  return (
    <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid={`range-weekdays-${rangeId}`}>
      <span className={labelClass + " mr-1"} style={{ color: "var(--text-muted)" }}>Días</span>
      {WEEKDAYS.map(([short, full], d) => {
        const active = weekdays.includes(d);
        return (
          <button key={full} type="button" title={full} aria-pressed={active} onClick={() => toggle(d)}
            data-testid={`range-weekday-${rangeId}-${d}`}
            className="h-8 w-8 rounded-full text-xs font-bold transition-colors"
            style={active ? { background: "var(--mint-dark)", color: "#fff" } : { background: "var(--bg)", color: "var(--text-muted)", textDecoration: "line-through" }}>
            {short}
          </button>
        );
      })}
    </div>
  );
}

function validRange(start, end) {
  if (!start || !end) { toast.error("Selecciona fecha de inicio y fin."); return false; }
  if (start > end) { toast.error("La fecha de inicio debe ser anterior a la de fin."); return false; }
  return true;
}

function SlotToggle({ rangeId, time, active, onToggle }) {
  const style = active
    ? { background: "var(--mint-light)", color: "var(--mint-dark)" }
    : { background: "var(--bg)", color: "var(--text-muted)", textDecoration: "line-through" };
  return (
    <button onClick={onToggle} data-testid={`range-slot-${rangeId}-${time}`} style={style}
      className="rounded-full px-4 py-2 text-sm font-semibold transition-colors">{time}</button>
  );
}

function RangeCard({ range, slots, onChange, onDelete }) {
  const setDate = (k) => (e) => onChange({ ...range, [k]: e.target.value });
  const toggleTime = (t) => {
    const active = range.times.includes(t);
    onChange({ ...range, times: active ? range.times.filter((x) => x !== t) : [...range.times, t].sort() });
  };

  return (
    <div className="rounded-2xl border border-[var(--border)] p-4" data-testid={`range-${range.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className={labelClass} style={{ color: "var(--text-muted)" }}>Del</span>
          <input type="date" data-testid={`range-start-${range.id}`} className={smallInput} value={range.start_date} onChange={setDate("start_date")} />
          <span className={labelClass} style={{ color: "var(--text-muted)" }}>al</span>
          <input type="date" data-testid={`range-end-${range.id}`} className={smallInput} value={range.end_date} onChange={setDate("end_date")} />
        </div>
        <button onClick={onDelete} data-testid={`delete-range-${range.id}`}
          className="ml-auto inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors hover:bg-red-50"
          style={{ color: "#991B1B" }}>
          <Trash2 size={14} /> Eliminar
        </button>
      </div>
      <WeekdayToggles rangeId={range.id} weekdays={range.weekdays || ALL_DAYS} onChange={(weekdays) => onChange({ ...range, weekdays })} />
      <div className="mt-3 flex flex-wrap gap-2">
        {slots.map((t) => <SlotToggle key={t} rangeId={range.id} time={t} active={range.times.includes(t)} onToggle={() => toggleTime(t)} />)}
      </div>
      {(range.times.length === 0 || (range.weekdays && range.weekdays.length === 0)) && (
        <p className="mt-2 text-xs" style={{ color: "#991B1B" }}>Sin horarios o días activos: este rango no ofrece citas.</p>
      )}
    </div>
  );
}

function NewRangeForm({ onAdd }) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const min = today();

  const add = () => {
    if (!validRange(start, end)) return;
    onAdd(start, end);
    setStart("");
    setEnd("");
  };

  return (
    <div className="mt-5 flex flex-wrap items-center gap-2">
      <input data-testid="new-range-start" type="date" min={min} className={smallInput + " py-3"} value={start} onChange={(e) => setStart(e.target.value)} />
      <span className="text-sm font-semibold" style={{ color: "var(--text-muted)" }}>al</span>
      <input data-testid="new-range-end" type="date" min={start || min} className={smallInput + " py-3"} value={end} onChange={(e) => setEnd(e.target.value)} />
      <button onClick={add} data-testid="add-range-button"
        className="inline-flex items-center gap-1 rounded-2xl px-4 py-3 text-sm font-semibold text-white transition-transform hover:scale-[1.03]"
        style={{ background: "var(--mint-dark)" }}><Plus size={16} /> Abrir rango</button>
    </div>
  );
}

export default function AvailabilityPanel({ onUnauthorized }) {
  const [av, setAv] = useState({ ranges: [], slots: [] });

  useEffect(() => {
    api.get("/availability").then(({ data }) => setAv(data)).catch((err) => console.warn("availability", err));
  }, []);

  const save = async (ranges) => {
    try {
      const { data } = await api.put("/availability", { ranges });
      setAv(data);
      toast.success("Disponibilidad actualizada");
    } catch (e) {
      if (e.response?.status === 401) { onUnauthorized(); return; }
      toast.error(e.response?.data?.detail || "No se pudo guardar la disponibilidad.");
    }
  };

  const addRange = (start, end) =>
    save([...av.ranges, { id: crypto.randomUUID(), start_date: start, end_date: end, times: [...(av.slots || [])], weekdays: [...ALL_DAYS] }]);
  const updateRange = (next) => {
    if (next.start_date && next.end_date && !validRange(next.start_date, next.end_date)) return;
    save(av.ranges.map((r) => (r.id === next.id ? next : r)));
  };
  const deleteRange = (id) => save(av.ranges.filter((r) => r.id !== id));

  return (
    <div className="rounded-[1.5rem] border border-[var(--border)] bg-white p-6 mb-6" data-testid="availability-panel">
      <div className="flex items-center gap-2">
        <CalendarOff size={18} style={{ color: "var(--mint-dark)" }} />
        <h2 className="font-display text-xl font-bold" style={{ color: "var(--text-dark)" }}>Fechas y horarios disponibles</h2>
      </div>
      <p className="mt-1 text-sm" style={{ color: "var(--text-muted)" }}>
        Abre rangos de fechas con sus días de la semana y horarios. Se atiende a un solo paciente por día. Si no hay rangos abiertos, nadie podrá reservar.
      </p>
      <NewRangeForm onAdd={addRange} />
      <div className="mt-5 grid gap-3">
        {av.ranges.length === 0 ? (
          <p className="rounded-2xl p-4 text-sm" style={{ background: "#FEF3C7", color: "#92400E" }} data-testid="no-ranges-notice">
            No hay fechas abiertas. Los pacientes verán "Por el momento no hay horarios disponibles".
          </p>
        ) : av.ranges.map((r) => (
          <RangeCard key={r.id} range={r} slots={av.slots || []} onChange={updateRange} onDelete={() => deleteRange(r.id)} />
        ))}
      </div>
      <p className="mt-3 text-xs" style={{ color: "var(--text-muted)" }}>Verde = día/horario abierto · Tachado = cerrado (toca para cambiar)</p>
    </div>
  );
}
